import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import AnnotationsModule from "highcharts/modules/annotations";
import {
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import { IOrderList } from "../../../Api/Interface/api.interface";
import { useGetAllPurDhashRecentOrderListQuery } from "../../../Api/attoDeskApi";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Pagination from "../../../Components/Pagination";
import { Link } from "react-router-dom";

AnnotationsModule(Highcharts);

const convertStatus = (unit: string): string => {
  switch (unit) {
    case "OrderListCreated":
      return "Draft";
    case "SentToManager":
      return "Pending Approval";
    case "ManagerApproved":
      return "Approved";
    case "UpdatedPrice":
      return "Updated Price";
    case "UpdatedStoreOnly":
      return "Updated Store";
    case "Processed":
      return "Processed";
    case "Completed":
      return "Completed";
    case "Rejected":
      return "Rejected";
    case "ReceiptUpdated":
      return "Receipt Updated";
    default:
      return unit;
  }
};

const AttendanceTimeline = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const {
    data: SentToManagerData,
    isLoading: SentToManagerIsLoading,
    isError: SentToManagerIsError,
  } = useGetAllPurDhashRecentOrderListQuery({
    searchText: "",
    orderListStatus: "",
    id: "",
  });

  const SentToManagerList: IOrderList[] = Array.isArray(SentToManagerData?.data)
    ? (SentToManagerData?.data as IOrderList[])
    : [];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <div
      style={{
        padding: "5px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", background: "#fafafa" }}>
                  <strong>Order ID</strong>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", background: "#fafafa" }}
                >
                  <strong>Status</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SentToManagerIsLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10vh"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : SentToManagerIsError || SentToManagerList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {SentToManagerIsError
                      ? "Error fetching data"
                      : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                SentToManagerList.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                ).map((row: IOrderList, index) => {
                  const orderLink =
                    row.orderListStatus === "OrderListCreated" ||
                    row.orderListStatus === "SentToManager" ||
                    row.orderListStatus === "ManagerApproved" ||
                    row.orderListStatus === "Rejected"
                      ? `/order/list/view/${row.id}`
                      : row.orderListStatus === "UpdatedPrice" ||
                        row.orderListStatus === "UpdatedStoreOnly" ||
                        row.orderListStatus === "Processed" ||
                        row.orderListStatus === "ReceiptUpdated"
                      ? `/order/list/set/price/list/${row.id}`
                      : "";

                  const editLink =
                    row.orderListStatus === "ManagerApproved"
                      ? `/order/list/price/admin/edit/${row.id}`
                      : row.orderListStatus === "SentToManager" ||
                        row.orderListStatus === "OrderListCreated"
                      ? `/order/list/edit/${row.id}`
                      : row.orderListStatus === "UpdatedPrice"
                      ? `/order/list/set/price/edit/${row.id}`
                      : "#";

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Link
                          to={orderLink}
                          className={classes.iconLinkPropNormal}
                        >
                          <Typography
                            sx={{
                              cursor: "pointer",
                            }}
                          >
                            {row.orderListId}
                          </Typography>
                        </Link>
                      </TableCell>

                      <TableCell scope="row" sx={{ textAlign: "center" }}>
                        <Grid container spacing={1}>
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            sx={{
                              display: "flex",
                              justifyContent: "center", // ✅ THIS
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Box
                              className={
                                row.orderListStatus === "OrderListCreated"
                                  ? classes.statusCreated
                                  : row.orderListStatus === "SentToManager"
                                  ? classes.statusSent
                                  : row.orderListStatus === "ManagerApproved"
                                  ? classes.statusApproved
                                  : row.orderListStatus === "UpdatedPrice"
                                  ? classes.statusUpdated
                                  : row.orderListStatus === "UpdatedStoreOnly"
                                  ? classes.statusUpdated
                                  : row.orderListStatus === "ManagerConfirmed"
                                  ? classes.statusConfirmed
                                  : row.orderListStatus === "Processed"
                                  ? classes.statusProcessed
                                  : row.orderListStatus === "Completed"
                                  ? classes.statusCompleted
                                  : row.orderListStatus === "ReceiptUpdated"
                                  ? classes.statusCompleted
                                  : row.orderListStatus === "Rejected"
                                  ? classes.statusRejected
                                  : ""
                              }
                            >
                              {convertStatus(row.orderListStatus)}
                            </Box>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};

export default AttendanceTimeline;
